import { Groq } from "groq-sdk";
import { toast } from "react-toastify";

export interface SelectedCampaign {
    ["additional-info"]?: string;
    ["brand-description"]: string;
    ["call-to-action"]: string;
    ["campaign-goal"]: string;
    ["campaign-name"]: string;
    ["key-messages"]: string;
    ["product-description"]: string;
    ["target-audience"]: string;
}

// Примерен обект SelectedCampaign на български
export const exampleCampaign: SelectedCampaign = {
    ["campaign-name"]: "Лятна промоция на еко-продукти",
    ["target-audience"]: "Съзнателни потребители на възраст 25-45 години, интересуващи се от екологично земеделие и устойчив начин на живот",
    ["product-description"]: "Натурални био-продукти за дома и градината",
    ["brand-description"]: "EcoLife - лидер в устойчивото земеделие в България", 
    ["campaign-goal"]: "Увеличаване на продажбите с 30% през лятния сезон и популяризиране на био-продуктите",
    ["key-messages"]: "Натуралност, екологичност, поддръжка на местни производители, здравословен начин на живот",
    ["call-to-action"]: "Направете поръчка до 31 август и получете безплатна доставка и 10% отстъпка",
    ["additional-info"]: "Всички продукти са сертифицирани по европейските стандарти за био-производство"
};

const emailPromptConfig = {
  systemPrompt: `Вие сте експерт копирайтър, специализиран в създаването на имейл маркетинг съдържание на български език.
Вашата задача е да създадете готово за използване съдържание, което:
- Не съдържа плейсхолдери или променливи
- Използва конкретни данни от предоставената информация
- Следва най-добрите практики за имейл маркетинг
- Спазва правилата на българския език
- Има ясна структура и форматиране
- Включва всички необходими елементи за успешен имейл
- Използва само обикновен текст без форматиране (без **, ##, или други маркерове)
- Спазва правилно форматиране с нови редове и интервали
- НЕ превежда имената на марките и брандовете`,
  formatInstructions: `
Структура на имейла (използвайте само обикновен текст):
1. Заглавие (Subject Line)
2. Приветствие
3. Основен текст с 2-3 параграфа
4. Списък с ключови предимства
5. Призив за действие
6. Подпис

Изисквания:
- Използвайте само обикновен текст без форматиране
- Не използвайте **, ## или други маркерове
- Използвайте конкретни данни от кампанията
- Не оставяйте празни места или плейсхолдери
- Включете всички важни детайли от предоставената информация
- Спазвайте правилата на българския език
- Използвайте правилна пунктуация
- Съгласувайте правилно прилагателните и съществителните
- Максимум 500 думи
- Използвайте нови редове за разделяне на секциите
- НЕ превеждайте имената на марките и брандовете`,
  grammarRules: `
Граматически правила:
- Използвайте активен залог
- Спазвайте правилната пунктуация
- Поддържайте кратки и ясни изречения
- Използвайте подходящ бизнес език
- Осигурете правилно форматиране
- Спазвайте правилата за членуване
- Използвайте правилни глаголни времена
- Избягвайте повтаряне на думи и фрази
- Използвайте нови редове за разделяне на параграфите
- Запазете оригиналните имена на марките и брандовете`
};

const adPromptConfig = {
  systemPrompt: `Вие сте експерт копирайтър, специализиран в създаването на рекламни послания на български език.
Вашата задача е да създадете силни и ефективни рекламни послания, като всяко едно:
- Създава незабавно впечатление с привличащо внимание заглавие
- Разказва убедителна история за продукта или услугата
- Създава емоционална връзка с целевата аудитория
- Представя ясни и конкретни предимства
- Включва силно призив за действие
- Не съдържа плейсхолдери или променливи
- Използва конкретни данни от предоставената информация
- Следва най-добрите практики за рекламни послания
- Спазва правилата на българския език
- Има ясна структура и форматиране
- Включва всички необходими елементи за успешно рекламно послание
- Използва само обикновен текст без форматиране (без **, ##, или други маркерове)
- Спазва правилно форматиране с нови редове и интервали
- НЕ превежда имената на марките и брандовете`,
  formatInstructions: `
Нов ред за всяко рекламно послание.

Изисквания:
- Използвайте само обикновен текст без форматиране
- Използвайте нов ред за всяко послание
- Не използвайте **, ## или други маркерове
- Използвайте конкретни данни от кампанията
- Не оставяйте празни места или плейсхолдери
- Спазвайте правилата на българския език
- Използвайте правилна пунктуация
- Съгласувайте правилно прилагателните и съществителните
- Максимум 500 думи
- Използвайте нови редове за разделяне на посланията
- НЕ превеждайте имената на марките и брандовете`,
  grammarRules: `
Граматически правила:
- Спазвайте правилната пунктуация
- Поддържайте кратки и ясни изречения
- Използвайте подходящ бизнес език
- Осигурете правилно форматиране
- Спазвайте правилата за членуване
- Използвайте правилни глаголни времена
- Избягвайте повтаряне на думи и фрази
- Използвайте нови редове за разделяне на параграфите
- Запазете оригиналните имена на марките и брандовете`
};

export const generateEmailText = async (
  campaign: SelectedCampaign = exampleCampaign,
): Promise<string> => {
  const apiKey = process.env.REACT_APP_GROQ_API_KEY;
  if (!apiKey) {
    throw new Error("API key is not defined");
  }

  const groq = new Groq({
    apiKey,
    dangerouslyAllowBrowser: true,
  });

  const campaignDetails = `
Детайли за кампанията:
- Име на кампанията: ${campaign["campaign-name"]}
- Продукт: ${campaign["product-description"]}
- Марка: ${campaign["brand-description"]}
- Целева аудитория: ${campaign["target-audience"]}
- Цел на кампанията: ${campaign["campaign-goal"]}
- Ключови съобщения: ${campaign["key-messages"]}
- Призив за действие: ${campaign["call-to-action"]}${campaign["additional-info"] ? `
- Допълнителна информация: ${campaign["additional-info"]}` : ''}`;

  const prompt = `Създайте готов за използване имейл маркетинг кампания със следните детайли:

${campaignDetails}

${emailPromptConfig.formatInstructions}

${emailPromptConfig.grammarRules}

Важно: 
- Създайте пълен, готов за използване имейл, който включва всички елементи от структурата
- Използвайте само обикновен текст без форматиране (без **, ##, или други маркерове)
- Не оставяйте плейсхолдери или празни места
- Използвайте нови редове за разделяне на секциите и параграфите
- Включете всички конкретни данни от предоставената информация`;

  try {
    const response = await groq.chat.completions.create({
      messages: [
        {
          role: "system",
          content: emailPromptConfig.systemPrompt,
        },
        {
          role: "user",
          content: prompt,
        }
      ],
      model: "gemma2-9b-it",
      temperature: 0.7,
      max_tokens: 500,
    });

    return response.choices[0]?.message?.content?.trim() || "";
  } catch (error) {
    toast.error("Неуспешно генериране на имейл съдържание. Моля, опитайте отново по-късно.");
    console.error("Error generating email:", error);
    return "";
  }
}

export const generateKeyAdMessages = async (
  campaign: SelectedCampaign = exampleCampaign,
): Promise<string[]> => {
  const apiKey = process.env.REACT_APP_GROQ_API_KEY;
  if (!apiKey) {
    throw new Error("API key is not defined");
  }

  const groq = new Groq({
    apiKey,
    dangerouslyAllowBrowser: true,
  });

  const campaignDetails = `
Детайли за кампанията:
- Име на кампанията: ${campaign["campaign-name"]}
- Продукт: ${campaign["product-description"]}
- Марка: ${campaign["brand-description"]}
- Целева аудитория: ${campaign["target-audience"]}
- Цел на кампанията: ${campaign["campaign-goal"]}
- Ключови съобщения: ${campaign["key-messages"]}
- Призив за действие: ${campaign["call-to-action"]}${campaign["additional-info"] ? `
- Допълнителна информация: ${campaign["additional-info"]}` : ''}`;

  const prompt = `Създайте 3 различни рекламни послания със следните детайли:

${campaignDetails}

Изисквания за всяко послание:
- Максимум 2 изречения
- Първото изречение трябва да привлече вниманието и да представи основната полза
- Второто изречение трябва да включва призив за действие
- Използвайте само обикновен текст без форматиране
- Не използвайте **, ## или други маркерове
- Използвайте конкретни данни от кампанията
- Не оставяйте празни места или плейсхолдери
- Спазвайте правилата на българския език
- Използвайте правилна пунктуация
- Съгласувайте правилно прилагателните и съществителните
- Максимум 30 думи за всяко послание
- НЕ превеждайте имената на марките и брандовете

Важно: 
- Създайте 3 различни рекламни послания с различни подходи:
  1. Първото послание да фокусира върху емоционалната връзка и ценностите
  2. Второто послание да фокусира върху функционалните предимства и ползите
  3. Третото послание да фокусира върху уникалността и конкурентните предимства
- Разделете посланията с "---"
- Включете всички конкретни данни от предоставената информация`;

  try {
    const response = await groq.chat.completions.create({
      messages: [
        {
          role: "system",
          content: adPromptConfig.systemPrompt,
        },
        {
          role: "user",
          content: prompt,
        }
      ],
      model: "gemma2-9b-it",
      temperature: 0.7,
      max_tokens: 300,
    });

    const content = response.choices[0]?.message?.content?.trim() || "";
    const messages = content.split("---").map(msg => msg.trim()).filter(msg => msg.length > 0);
    
    // Validate that we have exactly 3 messages
    if (messages.length !== 3) {
      toast.error("Неуспешно генериране на рекламни послания. Моля, опитайте отново.");
      return [];
    }

    return messages;
  } catch (error) {
    toast.error("Неуспешно генериране на рекламни послания. Моля, опитайте отново по-късно.");
    console.error("Error generating ads:", error);
    return [];
  }
}